<script lang="ts">
  import { getContext } from 'svelte';
  import { PhoneIcon } from './icons';
  import { sendEvent } from '../helpers/GoogleAnalytics';
  import type {
    ButtonInModel,
    GlobalProperties,
    Country,
  } from 'customer-components/lib/types';

  export let buttonTitle: ButtonInModel['label'];
  export let buttonLink: ButtonInModel['link'];
  export let buttonType: ButtonInModel['link_type'];
  export let isHeader = false;
  export let borderRadius: GlobalProperties['borderRadius'] = null;
  export let section = '';
  export let sx: string = '';
  export let isDisabled = false;
  export let eventName = null;

  const country = getContext<Country>('country') || 'us';
  const hash = getContext<string>('hash');
  const lpKey = getContext<string>('lpKey');

  function triggerEvent(name) {
    try {
      if (name) {
        sendEvent(name, {
          cta_text: buttonTitle,
          lpKey: lpKey,
          user_domain: window.location.hostname,
          section_clicked: section,
          country: country || 'us',
        });
      }
    } catch (err) {
      console.log(err);
    }
  }

  function handleAnchorClick() {
    if (eventName) triggerEvent(eventName);
  }

  function handleClick() {
    if (!eventName) return;
    triggerEvent(eventName);
  }
</script>

<!-- {#if settings} -->
{#if buttonType === 'External'}
  <a
    href={buttonLink}
    class="header-button {isHeader ? 'rounded' : ''}"
    on:click={handleClick}
    style="border-radius: {borderRadius || '5px'}; {sx}"
  >
    <span class="label">{buttonTitle}</span>
    <span class="screen-reader-text">{buttonLink}</span>
  </a>
{/if}

{#if buttonType === 'Section'}
  <a
    href="#{buttonLink}"
    class="header-button {isHeader ? 'rounded' : ''}"
    style="border-radius: {borderRadius || '5px'}; {sx}"
    on:click={handleAnchorClick}
  >
    <span class="label">{buttonTitle}</span>
    <span class="screen-reader-text">{buttonLink}</span>
  </a>
{/if}

{#if buttonType === 'Page'}
  <a
    href={buttonLink}
    class="header-button {isHeader ? 'rounded' : ''}"
    style="border-radius: {borderRadius || '5px'}; {sx}"
  >
    <span class="label">{buttonTitle}</span>
    <span class="screen-reader-text">{buttonLink}</span>
  </a>
{/if}

{#if buttonType === 'Email'}
  <a
    href={`mailto:${buttonLink}`}
    class="header-button {isHeader ? 'rounded' : ''}"
    on:click={handleClick}
    style="border-radius: {borderRadius || '5px'}; {sx}"
  >
    <span class="label">{buttonTitle}</span>
    <span class="screen-reader-text">{buttonLink}</span>
  </a>
{/if}

{#if buttonType === 'Phone'}
  <a
    href={`tel:${buttonLink}`}
    class="header-button {isHeader ? 'rounded' : ''}"
    on:click={handleClick}
    style="border-radius: {borderRadius || '5px'}; {sx}"
    on:click={ev => {
      if (country) {
        sendEvent(`${country}_Click_To_Call`, {
          hash,
        });
      }
      sendEvent('Click to call', {
        cta_text: buttonTitle,
        user_domain: window.location.hostname,
        lpKey: lpKey,
        section_clicked: section,
        set_country: country || 'us',
        phone_number: buttonLink,
      });
    }}
  >
    <span style="font-size:16px;"><PhoneIcon /></span>
    <span class="label-with-icon">{buttonTitle}</span>
  </a>
{/if}

{#if buttonType === 'submit'}
  <button
    type="submit"
    class="header-button {isHeader ? 'rounded' : ''}"
    class:isDisabled
    style=" border-radius: {borderRadius ||
      '5px'}; width:100%;margin:1em 0; {sx}"
    disabled={isDisabled}
    on:click={handleClick}
  >
    <span class="label-with-icon" style="letter-spacing:.5px;"
      >{buttonTitle}</span
    >
  </button>
{/if}

{#if buttonType === null}
  <!-- svelte-ignore a11y-invalid-attribute -->
  <a
    href="#"
    class="header-button {isHeader ? 'rounded' : ''}"
    on:click={handleClick}
    style=" border-radius: {borderRadius || '5px'}; {sx}"
  >
    <span class="label-with-icon">{buttonTitle}</span>
  </a>
{/if}

<!-- {/if} -->

<style>
  .header-button {
    width: auto;
    display: inline-flex;
    flex-shrink: 0;
    text-decoration: none;
    padding: 16px 40px;
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;
    align-items: center;
    text-align: center;
    justify-content: center;
    border-radius: 4px;
    background: var(--vlt-lp-accent);
    color: var(--vlt-lp-accent-text);
    font-family: var(--vlt-lp-paragraph-font, 'DM Sans');
    box-shadow: 0px 8px 15px rgba(0, 0, 0, 0.05);
    border: unset;
    cursor: pointer;
  }
  .label {
    text-align: center;
  }
  .header-button.rounded {
    padding: 15px 25px;
    font-weight: bold;
    font-size: 16px;
    border-radius: 25px;
    white-space: nowrap;
    background: var(--vlt-lp-primary, #5373f0);
    color: var(--vlt-lp-primary-text, #fff);
    font-family: var(--vlt-lp-paragraph-font, 'DM Sans');
  }
  .label-with-icon {
    margin-left: 8px;
  }
  .isDisabled {
    background-color: #c4c4c4;
  }
  @media screen and (max-width: 780px) {
    .header-button.rounded {
      display: flex;
    }
  }

  @media screen and (max-width: 640px) {
    .header-button {
      display: flex;
    }
  }

  .screen-reader-text {
    border: 0;
    clip: rect(1px, 1px, 1px, 1px);
    -webkit-clip-path: inset(50%);
    clip-path: inset(50%);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute !important;
    width: 1px;
    word-wrap: normal !important;
    word-break: normal;
  }
</style>
